import { useMemo } from 'react'

import { createPortal } from 'react-dom'

import { Grid, GridSize } from '@mui/material'
import { Field, FieldProps, useFormikContext } from 'formik'
import isFunction from 'lodash/isFunction'

import {
  Form,
  FieldLayoutItem,
  LayoutItem,
  ElementLayoutItem,
  FormLayoutProps,
  FormHelpersProviderReturn,
} from '../../../ana-toolbox'
import { createField } from '../../utils/helpers/createField'
import { getIsRequiredFromFunction } from '../../utils/helpers/getIsRequired'
import { useFormHelpers, FormHelpersProvider } from '../../utils/hooks/useFormHelpers'
import { useFormValue, FormValueProvider } from '../../utils/hooks/useFormValue'
import { SubmitCountProvider } from '../../utils/hooks/useSubmitCount'

import * as S from './form-layout.styles'

const FormLayoutItem = ({ field, xs, sm, ...gridProps }: FieldLayoutItem) => {
  if (!field) return null
  return (
    <Grid size={{ xs: xs as GridSize, sm: sm as GridSize }} {...gridProps}>
      {createField(field)}
    </Grid>
  )
}

const FormLayoutItemWithValues = ({ field, ...gridProps }: FieldLayoutItem) => {
  const { values } = useFormikContext<Partial<Form>>()

  const canShow = useMemo(() => {
    if (!field?.visibleWhen) return true

    return field.visibleWhen(values)
  }, [values, field])

  const isRequired = useMemo(() => {
    if (typeof field?.isRequired !== 'function') return field?.isRequired

    return getIsRequiredFromFunction(values, field.isRequired)
  }, [values, field])

  const optimizedField = useMemo(() => {
    return { ...field, isRequired }
  }, [field, isRequired])

  if (!canShow) return null

  return <FormLayoutItem field={optimizedField} {...gridProps} />
}

const CustomFormLayoutItem = ({ render, xs, sm, ...props }: ElementLayoutItem) => {
  const formValues = useFormValue()
  const formHelpers = useFormHelpers()
  const isPromise = (val: unknown): val is Promise<unknown> => typeof (val as Promise<unknown>)?.then === 'function'
  const maybeChildren = render({ values: formValues, formHelpers: formHelpers as FormHelpersProviderReturn })

  if (!maybeChildren || isPromise(maybeChildren)) return null

  return (
    <Grid {...props} size={{ xs: xs as GridSize, sm: sm as GridSize }}>
      {maybeChildren}
    </Grid>
  )
}

/* This component is used only during tests to detect whther formik is doing some
 * background work that might cause an change outside `act`. This is needed to
 * prevent the warnings while the test is running
 * */
export const TestHelper = () => {
  if (process.env.NODE_ENV !== 'test') return null

  return (
    <Field>
      {({ form }: FieldProps) =>
        form.isValidating || form.isSubmitting ? <div data-testid="formik-test-helper" aria-hidden="true" /> : null
      }
    </Field>
  )
}

export const FormLayoutItemRenderer = (item: LayoutItem) => {
  const { type, key } = item

  if (type === 'field') {
    if (isFunction(item.field.visibleWhen) || isFunction(item.field.isRequired))
      return <FormLayoutItemWithValues key={item.field.name} {...item} />
    return <FormLayoutItem key={item.field.name} {...item} />
  }

  if (type === 'custom') return <CustomFormLayoutItem {...item} key={key} />

  return null
}

export const FormLayout = ({ items, portalAttr, submitButton, spacingX, spacingY }: FormLayoutProps) => {
  const portalNodes = portalAttr && document.querySelectorAll(`[data-attr="${portalAttr}"]`)

  return (
    <SubmitCountProvider>
      <FormValueProvider>
        <FormHelpersProvider>
          <S.Container spacingX={spacingX || 3} spacingY={spacingY || 3}>
            {items.map(FormLayoutItemRenderer)}
            {submitButton && portalNodes && portalNodes.length > 0 ? (
              Array.from(portalNodes).map((node) => createPortal(submitButton, node))
            ) : (
              <Grid container justifyContent="center" size={12}>
                {submitButton}
              </Grid>
            )}
            <TestHelper />
          </S.Container>
        </FormHelpersProvider>
      </FormValueProvider>
    </SubmitCountProvider>
  )
}
